/**
 * Permission gate for analytics data queries.
 *
 * Every dashboard data query (the methods routed through dataClient's
 * useQuery / usePagedDimEntries) lowers to an api-server RPC that the backend
 * gates on `tenant.analytics.execute`. A caller resolved to lack that
 * permission can only ever get a 403 back, so the data hooks consult this
 * module to skip the request entirely — otherwise a restricted role (e.g. an
 * IT-ops role with fleet access but no analytics) fills the dev-tools console
 * with denials just by loading the default view.
 *
 * Kept as a leaf module (depends only on the permission predicate + the
 * permission vocabulary) so the gate is unit-testable without dragging in the
 * full analytics client graph.
 */
import { hasPermission } from "./permissions.js";
import { PERMISSIONS } from "./permissionKeys.js";

// Methods that must fire regardless of the caller's permissions. `init` is the
// analytics schema-readiness probe: boot needs its result to detect a
// degraded-analytics posture, and it already treats a 403 as "ready, no
// analytics" rather than an error.
const PERMISSION_EXEMPT_METHODS = new Set(["init"]);

/**
 * The permission a query method needs before it's worth firing, or null if it
 * has no gate. Every query method other than the exempt probes is analytics-
 * backed, so the requirement is uniformly `tenant.analytics.execute`.
 *
 * @param {string} method — query method name
 * @returns {string|null}
 */
export function methodRequiredPermission(method) {
  if (PERMISSION_EXEMPT_METHODS.has(method)) return null;
  return PERMISSIONS.ANALYTICS_EXECUTE;
}

/**
 * Whether a query method is worth firing for the given permission posture.
 *
 * `hasPermission` fails OPEN while caps are loading / degraded / pre-RBAC, so
 * this only suppresses queries for a role we KNOW lacks the permission; a real
 * analytics user mid-load keeps firing and never sees a blanked view.
 *
 * @param {string} method — query method name
 * @param {object} caps — the resolved permission posture from useMyPermissions
 * @returns {boolean}
 */
export function queryPermitted(method, caps) {
  const requiredPerm = methodRequiredPermission(method);
  return !requiredPerm || hasPermission(caps, requiredPerm);
}
